import { io } from 'socket.io-client';
import React, { useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import './KanbanView.css';

// New component for the draggable console modal
const DraggableConsole = ({ logs, show, onClose }) => {
    const [position, setPosition] = useState({ x: 20, y: 70 });
    const [size, setSize] = useState({ width: 600, height: 300 });
    const [dragging, setDragging] = useState(false);
    const [resizing, setResizing] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [autoScroll, setAutoScroll] = useState(true);
    const logsEndRef = useRef(null);
    const logsContainerRef = useRef(null);

    useEffect(() => {
        if (autoScroll && show) {
            setTimeout(() => {
                logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    }, [logs, autoScroll, show]);

    const handleMouseDown = (e) => {
        if (e.target.className.includes('console-header')) {
            setDragging(true);
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            });
        }
    };

    const handleResizeStart = (e) => {
        e.stopPropagation();
        setResizing(true);
        setDragStart({
            x: e.clientX,
            y: e.clientY
        });
    };

    const handleMouseMove = (e) => {
        if (dragging) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        } else if (resizing) {
            setSize({
                width: size.width + (e.clientX - dragStart.x),
                height: size.height + (e.clientY - dragStart.y)
            });
            setDragStart({
                x: e.clientX,
                y: e.clientY
            });
        }
    };

    const handleMouseUp = () => {
        setDragging(false);
        setResizing(false);
    };

    useEffect(() => {
        if (dragging || resizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragging, resizing]);

    if (!show) return null;

    return (
        <div
            className="console-modal"
            style={{
                position: 'fixed',
                left: `${position.x}px`,
                top: `${position.y}px`,
                width: `${size.width}px`,
                height: `${size.height}px`,
                zIndex: 1000,
                backgroundColor: '#1e1e1e',
                color: '#f8f8f8',
                border: '1px solid #444',
                borderRadius: '4px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
            onMouseDown={handleMouseDown}
        >
            <div
                className="console-header"
                style={{
                    padding: '8px 12px',
                    backgroundColor: '#333',
                    cursor: 'move',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    userSelect: 'none'
                }}
            >
                <span>Console Logs</span>
                <button
                    onClick={onClose}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '16px'
                    }}
                >✕</button>
            </div>
            <div
                className="console-content"
                style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: '10px',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap'
                }}
                onScroll={(e) => {
                    const { scrollTop, scrollHeight, clientHeight } = e.target;
                    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
                    setAutoScroll(isAtBottom);
                }}
                ref={logsContainerRef}
            >
                {logs.join('\n')}
                <div ref={logsEndRef} />
            </div>
            <div
                className="resize-handle"
                style={{
                    position: 'absolute',
                    right: '0',
                    bottom: '0',
                    width: '10px',
                    height: '10px',
                    cursor: 'nwse-resize',
                    background: 'transparent'
                }}
                onMouseDown={handleResizeStart}
            />
        </div>
    );
};

const CorrectionForm = ({ message, conversationId, conversation }) => {
    const [corrected, setCorrected] = useState('');
    const [saved, setSaved] = useState(false);
    const [loading, setLoading] = useState(false);
    const [contextCount, setContextCount] = useState(5); // default: 5 messages

    const getContextMessages = () => {
        if (!conversation || !conversation.messages) return [];

        const index = conversation.messages.findIndex(m => m.id === message.id);
        const start = Math.max(0, index - contextCount);
        const slice = conversation.messages.slice(start, index + 1);

        return slice.map(m => ({
            sender: m.sender,
            message: m.message,
            timestamp: m.timestamp
        }));
    };


    const handleCorrection = async () => {
        if (!corrected.trim()) return;
        setLoading(true);

        try {
            const contextJson = JSON.stringify(getContextMessages());

            await fetch('https://adam.app.autofront.com.au/api/corrections', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message_id: message.id,
                    conversation_id: conversationId,
                    original_response: message.message,
                    corrected_response: corrected,
                    correction_reason: 'manual fix',
                    corrected_by: 'admin@alpha-air.com.au',
                    category: conversation?.category || 'other',
                    context_json: contextJson
                })
            });

            setSaved(true);
            setCorrected('');
            setTimeout(() => setSaved(false), 4000);
        } catch (err) {
            console.error("Failed to save correction:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ marginBottom: '12px', borderTop: '1px solid #ccc', paddingTop: '8px' }}>
            <div style={{ fontSize: '13px', color: '#455', marginBottom: '4px' }}>
                <strong>Adam resplied:</strong>{' '}
                <span style={{ color: '#fff' }}>{message.message}</span>
            </div>

            <textarea
                placeholder="Enter corrected response"
                value={corrected}
                onChange={(e) => setCorrected(e.target.value)}
                rows={2}
                style={{
                    width: '100%',
                    fontSize: '13px',
                    border: '1px solid #ccc',
                    padding: '4px',
                    borderRadius: '4px'
                }}
            />

            <div style={{ marginTop: '6px', marginBottom: '6px' }}>
                <label style={{ fontSize: '12px', marginRight: '8px' }}>
                    Include previous messages:
                </label>
                <select
                    value={contextCount}
                    onChange={(e) => setContextCount(parseInt(e.target.value))}
                    style={{ fontSize: '12px' }}
                >
                    {[...Array(10).keys()].map(i => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                </select>
            </div>

            <button
                onClick={handleCorrection}
                disabled={loading}
                style={{
                    backgroundColor: '#1976d2',
                    color: '#fff',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                }}
            >
                {loading ? 'Saving...' : 'Save Correction'}
            </button>
            {saved && <span style={{ marginLeft: '10px', color: 'green' }}>✔ Saved</span>}
        </div>
    );
};


const KanbanView = ({ showConsole, setShowConsole }) => {
    const [columns, setColumns] = useState({});
    const [selectedConvo, setSelectedConvo] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [logs, setLogs] = useState([]);
    const [highlightedCardId, setHighlightedCardId] = useState(null);

    const socketRef = useRef(null);


    const messageEndRef = useRef(null);
    const selectedConvoRef = useRef(null);

    const fetchData = async () => {
        try {
            const res = await fetch('https://adam.app.autofront.com.au/api/conversations');
            const data = await res.json();
            console.log('Fetched conversations:', data); // Debugging log

            const grouped = {
                sales: [],
                service: [],
                warranty: [],
                other: [],
                human_required: [],
            };

            data.forEach((c) => {
                // Ensure category is a valid string
                const key = c.status === 'human_required'
                    ? 'human_required'
                    : (typeof c.category === 'string' && c.category) || 'other';

                if (!grouped[key]) grouped[key] = [];
                grouped[key].push(c);
            });

            setColumns(grouped);

            if (selectedConvoRef.current) {
                const updated = data.find((d) => d.id === selectedConvoRef.current.id);
                if (updated) {
                    setSelectedConvo({ ...updated });
                    selectedConvoRef.current = { ...updated };
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const socket = io('https://adam.app.autofront.com.au');
        socketRef.current = socket;

        socket.on('connect', () => {
            console.log('✅ Socket.IO connected');
        });

        // Listen for new conversation events
        socket.on('conversation:new', (conversation) => {
            console.log('📥 New conversation received:', conversation);

            setColumns((prevColumns) => {
                const updatedColumns = { ...prevColumns };
                const key = conversation.status === 'human_required'
                    ? 'human_required'
                    : (typeof conversation.category === 'string' && conversation.category) || 'other';

                if (!updatedColumns[key]) updatedColumns[key] = [];
                updatedColumns[key].push(conversation);

                return updatedColumns;
            });
        });

        socket.on('message:new', (data) => {
            setHighlightedCardId(data.conversationId);
            setTimeout(() => setHighlightedCardId(null), 5000); // 5-second highlight

            console.log('📥 Socket received:', data);
            if (!data || !data.conversationId || !data.message) return;

            setColumns(prevColumns => {
                const updated = { ...prevColumns };
                for (const key in updated) {
                    updated[key] = updated[key].map(c => {
                        if (c.id === data.conversationId) {
                            const newMsg = {
                                id: data.id || Date.now(),
                                sender: data.sender,
                                message: data.message,
                                timestamp: data.timestamp
                            };
                            const updatedConvo = {
                                ...c,
                                messages: [...(c.messages || []), newMsg],
                                last_activity: data.timestamp
                            };
                            return updatedConvo;
                        }
                        return c;
                    });
                }
                return updated;
            });

            if (selectedConvoRef.current?.id === data.conversationId) {
                const newMsg = {
                    id: data.id || Date.now(),
                    sender: data.sender,
                    message: data.message,
                    timestamp: data.timestamp,
                };

                const updatedMessages = [...(selectedConvoRef.current.messages || []), newMsg];
                const updatedConvo = {
                    ...selectedConvoRef.current,
                    messages: updatedMessages,
                    last_activity: data.timestamp,
                };

                setSelectedConvo(updatedConvo);
                selectedConvoRef.current = updatedConvo;
            }
        });

        socket.on('conversation:categoryUpdated', (data) => {
            console.log('📂 Detected category change:', data);
            fetchData(); // Re-fetch full conversation list to update Kanban columns
        });

        socket.on('log:update', (message) => {
            setLogs((prev) => {
                const updated = [...prev, message];
                return updated.slice(-500);
            });
        });

        socket.on('conversation:nameUpdated', (data) => {
            setColumns(prevColumns => {
                const updated = { ...prevColumns };
                for (const key in updated) {
                    updated[key] = updated[key].map(c =>
                        c.id === data.conversationId
                            ? { ...c, name: data.newName }
                            : c
                    );
                }
                return updated;
            });

            // Also update selectedConvo if it's open
            if (selectedConvoRef.current?.id === data.conversationId) {
                setSelectedConvo(prev => prev ? { ...prev, name: data.newName } : prev);
                selectedConvoRef.current = { ...selectedConvoRef.current, name: data.newName };
            }
        });

        return () => socket.disconnect();
    }, []);

    useEffect(() => {
        if (selectedConvo) {
            setTimeout(() => {
                messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    }, [selectedConvo]);

    const onDragEnd = async (result) => {
        const { source, destination, draggableId } = result;
        if (!destination || source.droppableId === destination.droppableId) return;
        const convoId = parseInt(draggableId);
        const newStatus = destination.droppableId;
        await fetch(`https://adam.app.autofront.com.au/api/conversations/${convoId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ category: newStatus }),
        });
        fetchData();
    };

    const formatRelativeTime = (timestamp) => {
        const then = new Date(timestamp);
        const now = new Date();
        const diffMs = now - then;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return '#2ecc71'; // green
            case 'inactive':
                return '#e67e22'; // orange
            case 'archived':
                return '#8e5e3a'; // brown
            case 'human_required':
                return '#e74c3c'; // red
            default:
                return '#7f8c8d'; // grey fallback
        }
    };

    const formatStatus = (status) => {
        switch (status) {
            case 'active':
                return 'Active';
            case 'inactive':
                return 'Inactive';
            case 'archived':
                return 'Archived';
            case 'human_required':
                return 'Escalated';
            default:
                return 'Unknown';
        }
    };

    const renderColumn = (id, title) => (
        <Droppable droppableId={id} key={id}>
            {(provided) => (
                <div className={`kanban-column ${id}`} ref={provided.innerRef} {...provided.droppableProps}>
                    <h3 className={`kanban-column-header ${id}`}>{title}</h3>
                    <div className="kanban-column-content">
                        {columns[id]?.map((c, i) => (
                            <Draggable draggableId={c.id.toString()} index={i} key={c.id}>
                                {(provided) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className={`kanban-card ${highlightedCardId === c.id ? 'highlight' : ''}`}
                                        onClick={() => {
                                            setSelectedConvo(c);
                                            selectedConvoRef.current = c;
                                            setTimeout(() => {
                                                messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                                            }, 100);
                                        }}
                                    >
                                        <div className="card-name">{c.name || 'Unknown'}</div>
                                        <div className="card-phone">{c.phone}</div>
                                        <div className="card-msg">{c.messages?.at(-1)?.message}</div>
                                        <div
                                            className="card-status-tag"
                                            style={{
                                                display: 'inline-block',
                                                padding: '2px 8px',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                backgroundColor: getStatusColor(c.status),
                                                color: 'white',
                                                marginTop: '6px',
                                                alignSelf: 'flex-start'
                                            }}
                                        >
                                            {formatStatus(c.status)}
                                        </div>

                                        <div className="card-timestamp">
                                            {c.last_activity &&
                                                new Date(c.last_activity).toLocaleString('en-AU', {
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    hour12: true,
                                                    weekday: 'short',
                                                })}
                                        </div>
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                </div>
            )}
        </Droppable>
    );

    const sendReply = async () => {
        if (!replyText.trim() || !selectedConvo) return;
        try {
            await fetch(`https://adam.app.autofront.com.au/api/conversations/${selectedConvo.id}/reply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: replyText }),
            });

            // ✅ Emit update to socket.io for real-time refresh
            socketRef.current?.emit('message:new', {
                conversationId: selectedConvo.id,
                message: replyText,
                sender: 'bot',
                timestamp: new Date().toISOString()
            });

            setReplyText('');
        } catch (err) {
            console.error('Failed to send reply:', err);
        }
    };

    const formatAustralianMobile = (phone) => {
        if (!phone.startsWith('+61')) return phone; // fallback
        const local = '0' + phone.slice(3); // +614XXXXXXXX → 04XXXXXXXX
        return local.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
    };


    return (
        <div className="kanban-wrapper">
            {/* Nav Bar with Console Toggle Button */}


            <DraggableConsole
                logs={logs}
                show={showConsole}
                onClose={() => setShowConsole(false)}
            />

            <DragDropContext onDragEnd={onDragEnd}>
                <div className={`kanban-board ${selectedConvo ? 'with-panel' : ''}`}>
                    {renderColumn('sales', 'Sales')}
                    {renderColumn('service', 'Service / Breakdown')}
                    {renderColumn('warranty', 'Warranty')}
                    {renderColumn('other', 'Other')}
                    {renderColumn('human_required', 'Escalate')}
                </div>
            </DragDropContext>

            {selectedConvo && (
                <div className="conversation-panel">
                    <div className="conversation-header">
                        <h4>
                            Conversation with
                            <input
                                type="text"
                                value={selectedConvo.name || ''}
                                onChange={(e) => setSelectedConvo({ ...selectedConvo, name: e.target.value })}
                                placeholder="Enter new name"
                                className="update-name-input"
                            />
                            <button
                                className="save-name-button"
                                onClick={async () => {
                                    if (!selectedConvo.name.trim()) return;
                                    try {
                                        await fetch(`https://adam.app.autofront.com.au/api/conversations/${selectedConvo.id}`, {
                                            method: 'PUT',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ name: selectedConvo.name }),
                                        });
                                        fetchData();
                                    } catch (err) {
                                        console.error('Failed to update name:', err);
                                    }
                                }}
                            >
                                Save
                            </button>
                            <div style={{ fontSize: '14px', color: '#555' }}>
                                📞 <strong>{formatAustralianMobile(selectedConvo.phone)}</strong>
                            </div>
                        </h4>
                        <button className="close-button" onClick={() => setSelectedConvo(null)}>✕</button>
                    </div>

                    <div className="conversation-messages">
                        {selectedConvo.messages?.map((msg) => (
                            <div key={msg.id} className={`message ${msg.sender}`}>
                                <div className="message-content">
                                    {msg.sender === 'bot' ? (
                                        <CorrectionForm
                                            message={msg}
                                            conversationId={selectedConvo.id}
                                            conversation={selectedConvo}
                                        />

                                    ) : (
                                        msg.message
                                    )}
                                </div>
                                <div className="message-meta">{formatRelativeTime(msg.timestamp)}</div>
                            </div>
                        ))}
                        <div ref={messageEndRef} />
                    </div>

                    <button
                        className="delete-button"
                        onClick={async () => {
                            const confirmed = window.confirm('Are you sure you want to delete this conversation?');
                            if (!confirmed) return;
                            await fetch(`https://adam.app.autofront.com.au/api/conversations/${selectedConvo.id}`, {
                                method: 'DELETE',
                            });
                            setSelectedConvo(null);
                            selectedConvoRef.current = null;
                            fetchData();
                        }}
                    >
                        🗑 Delete Conversation
                    </button>

                    <div className="change-cat">
                        <label>Set Category:</label>
                        <select
                            defaultValue={selectedConvo.category}
                            onChange={async (e) => {
                                await fetch(`https://adam.app.autofront.com.au/api/conversations/${selectedConvo.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ category: e.target.value }),
                                });
                                fetchData();
                            }}
                        >
                            <option value="sales">Sales</option>
                            <option value="service">Service</option>
                            <option value="warranty">Warranty</option>
                            <option value="other">Other</option>
                            <option value="human_required">Escalate</option>
                        </select>
                    </div>

                    {selectedConvo?.status === 'human_required' && (
                        <div style={{ marginTop: '10px' }}>
                            <button
                                onClick={async () => {
                                    try {
                                        await fetch(`https://adam.app.autofront.com.au/api/conversations/${selectedConvo.id}/clear-escalation`, {
                                            method: 'PATCH'
                                        });
                                        fetchData(); // Refresh to reflect new status
                                    } catch (err) {
                                        console.error('Failed to clear escalation:', err);
                                    }
                                }}
                                style={{
                                    backgroundColor: '#f39c12',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '8px 14px',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    marginTop: '6px'
                                }}
                            >
                                🔓 Clear Escalation
                            </button>
                        </div>
                    )}


                    <div className="reply-box" style={{ marginTop: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                            Reply as Adam:
                        </label>

                        <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            style={{
                                width: '100%',
                                minHeight: '80px',
                                fontSize: '14px',
                                border: '1px solid #ccc',
                                borderRadius: '6px',
                                padding: '10px',
                                resize: 'vertical',
                                boxSizing: 'border-box',
                                marginBottom: '12px'
                            }}
                            placeholder="Type your reply here..."
                        />

                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={sendReply}
                                style={{
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '10px 18px',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                    transition: 'background-color 0.2s ease'
                                }}
                            >
                                Send <span className="send-icon">✉</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KanbanView;